import React, { useState, useEffect } from 'react';
import { Search, Receipt, CreditCard, Calendar, User, IndianRupee, Filter } from 'lucide-react';
import { Payment, StudentFee } from '../../types';
import { feesApi, studentsApi } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { toast } from 'react-toastify';

interface TransactionRecord extends Payment {
  studentName: string;
  studentId: string;
  className: string;
}

const AllTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<TransactionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);

      // Fetch all student fees and students data in parallel
      const [allStudentFees, allStudents] = await Promise.all([
        feesApi.getAllStudentFees(),
        studentsApi.getAll()
      ]);

      // Create a map for quick student lookup
      const studentMap = new Map(allStudents.map(student => [student.id, student]));

      // Extract all payments with student info
      const allTransactions: TransactionRecord[] = [];

      allStudentFees.forEach(studentFee => {
        const student = studentMap.get(studentFee.studentId);

        if (student && studentFee.payments) {
          studentFee.payments.forEach(payment => {
            allTransactions.push({
              ...payment,
              studentName: student.name,
              studentId: student.studentId,
              className: studentFee.className
            });
          });
        }
      });

      // Sort by date (newest first)
      allTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setTransactions(allTransactions);
    } catch (error) {
      toast.error('Failed to load transaction history');
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentModeIcon = (mode: string) => {
    switch (mode) {
      case 'Cash':
        return <IndianRupee className="w-4 h-4" />;
      case 'Online':
      case 'Bank Transfer':
        return <CreditCard className="w-4 h-4" />;
      default:
        return <Receipt className="w-4 h-4" />;
    }
  };

  const getPaymentModeColor = (mode: string) => {
    switch (mode) {
      case 'Cash':
        return 'bg-[#173322] text-[#8ff0b8]';
      case 'Online':
        return 'bg-[#16212b] text-[#bfdbfe]';
      case 'Bank Transfer':
        return 'bg-info-100 text-info-800';
      case 'Cheque':
        return 'bg-[#332711] text-[#f9daa3]';
      default:
        return 'bg-[#b9ad9b] text-[#4a4235]';
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch =
      transaction.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.receiptNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (transaction.transactionId && transaction.transactionId.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesMode = !filterMode || transaction.mode === filterMode;

    const transactionDate = new Date(transaction.date);
    const matchesDateFrom = !dateFrom || transactionDate >= new Date(dateFrom);
    const matchesDateTo = !dateTo || transactionDate <= new Date(dateTo);

    return matchesSearch && matchesMode && matchesDateFrom && matchesDateTo;
  });

  const totalAmount = filteredTransactions.reduce((sum, transaction) => sum + transaction.amount, 0);

  return (
    <div className="space-y-6">
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
        <div className="p-6 border-b border-[#3a3122]">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8a7f6e]" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent w-full"
              />
            </div>

            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
            >
              <option value="">All Payment Modes</option>
              <option value="Cash">Cash</option>
              <option value="Online">Online</option>
              <option value="Cheque">Cheque</option>
              <option value="Bank Transfer">Bank Transfer</option>
            </select>

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              placeholder="From date"
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
            />

            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              placeholder="To date"
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
            />
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-[#b9ad9b]">
              Showing {filteredTransactions.length} transactions • Total: ₹{totalAmount.toLocaleString()}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
            <p className="text-[#b9ad9b] text-lg">No transactions found</p>
            <p className="text-[#8a7f6e] text-sm">Try adjusting your search criteria</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#211c15]">
                <tr>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Date</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Receipt</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Student</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Amount</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Mode</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Transaction ID</th>
                  <th className="text-left py-3 px-6 font-medium text-[#b9ad9b]">Created By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3a3122]">
                {filteredTransactions.map((transaction, index) => (
                  <tr key={`${transaction.receiptNo}-${index}`} className="hover:bg-[#28221a]">
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-[#8a7f6e]" />
                        <span className="text-[#b9ad9b]">{formatDate(transaction.date)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <Receipt className="w-4 h-4 text-[#8a7f6e]" />
                        <span className="font-mono text-[#f3ede2]">{transaction.receiptNo}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-[#8a7f6e]" />
                        <div>
                          <div className="font-medium text-[#f3ede2]">{transaction.studentName}</div>
                          <div className="text-sm text-[#b9ad9b]">
                            ID: {transaction.studentId} • Class: {transaction.className}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <IndianRupee className="w-4 h-4 text-[#4ade80]" />
                        <span className="font-semibold text-[#6ee7a0]">
                          ₹{transaction.amount.toLocaleString()}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium flex items-center space-x-1 w-fit ${getPaymentModeColor(transaction.mode)}`}>
                        {getPaymentModeIcon(transaction.mode)}
                        <span>{transaction.mode}</span>
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-[#b9ad9b] text-sm font-mono">
                        {transaction.transactionId || '-'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-[#b9ad9b] text-sm">{transaction.createdBy}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllTransactions;